import { useState, useMemo, useCallback } from "react";
import {
  Box,
  Chip,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Dialog } from "components/Dialog";

interface WasteCodeCompareDialogProps {
  open: boolean;
  onClose: () => void;
  wasteCodes: string[];
}

function parseWasteCodes(input: string): string[] {
  // Split by commas, spaces, tabs, newlines, and pipe characters
  return input
    .split(/[\s,|\t\n\r]+/)
    .map((code) => code.trim().toUpperCase())
    .filter((code) => code.length > 0);
}

export function WasteCodeCompareDialog({
  open,
  onClose,
  wasteCodes,
}: WasteCodeCompareDialogProps) {
  const theme = useTheme();
  const [userInput, setUserInput] = useState("");

  const handleClose = useCallback(() => {
    setUserInput("");
    onClose();
  }, [onClose]);

  const encampCodes = useMemo(
    () => wasteCodes.map((c) => c.toUpperCase()),
    [wasteCodes]
  );

  const userCodes = useMemo(() => parseWasteCodes(userInput), [userInput]);

  const inEncampNotInUser = useMemo(() => {
    if (userCodes.length === 0) return [];
    const userSet = new Set(userCodes);
    return encampCodes.filter((code) => !userSet.has(code));
  }, [encampCodes, userCodes]);

  const inUserNotInEncamp = useMemo(() => {
    if (userCodes.length === 0) return [];
    const encampSet = new Set(encampCodes);
    return userCodes.filter((code) => !encampSet.has(code));
  }, [encampCodes, userCodes]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      title="Compare Waste Codes"
    >
      <Stack direction="row" spacing={3} sx={{ mt: 1, alignItems: "stretch" }}>
        {/* Left: Encamp Codes */}
        <Stack spacing={2} sx={{ flex: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Waste Codes in Encamp ({encampCodes.length})
          </Typography>
          <Box
            sx={{
              p: 2,
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 1,
              height: 200,
              overflow: "auto",
              backgroundColor: theme.palette.grey[50],
            }}
          >
            <Typography
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}
            >
              {encampCodes.length > 0
                ? encampCodes.join("  ")
                : "No waste codes found"}
            </Typography>
          </Box>

          <Box sx={{ pt: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              In Encamp but NOT in your list ({inEncampNotInUser.length})
            </Typography>
            <Box
              sx={{
                minHeight: 100,
                p: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {inEncampNotInUser.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {inEncampNotInUser.map((code) => (
                    <Chip
                      key={code}
                      label={code}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : userCodes.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  All Encamp codes are in your list
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add waste codes to compare
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>

        {/* Right: User Codes */}
        <Stack spacing={2} sx={{ flex: 1 }}>
          <Typography variant="subtitle2" color="text.secondary">
            Your list ({userCodes.length} codes parsed)
          </Typography>
          <TextField
            multiline
            fullWidth
            placeholder="Paste waste codes here (space, comma, tab, or newline separated)"
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            sx={{
              "& .MuiInputBase-root": {
                height: 200,
                alignItems: "flex-start",
                p: 2,
                overflow: "auto",
              },
              "& .MuiInputBase-input": {
                p: 0,
              },
            }}
          />

          <Box sx={{ pt: 1 }}>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              In your list but NOT in Encamp ({inUserNotInEncamp.length})
            </Typography>
            <Box
              sx={{
                minHeight: 100,
                p: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1,
              }}
            >
              {inUserNotInEncamp.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={0.5}>
                  {inUserNotInEncamp.map((code) => (
                    <Chip
                      key={code}
                      label={code}
                      size="small"
                      color="error"
                      variant="outlined"
                    />
                  ))}
                </Stack>
              ) : userCodes.length > 0 ? (
                <Typography variant="body2" color="text.secondary">
                  All your codes are in Encamp
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Add waste codes to compare
                </Typography>
              )}
            </Box>
          </Box>
        </Stack>
      </Stack>
    </Dialog>
  );
}
